import { Link, useLocation } from "react-router-dom";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import useLoadContentByIdOrSlug from "../../hooks/useLoadContentById";
import { toTitleCase } from "../../lib/string";
import { getContacts } from "../../api/relationships.api";
import { ContactRow } from "../../api/relationship.type";
import { useAuth } from "../../hooks/AuthContext";
import { getGroupChat } from "../../api/groupChats.api";
import { qk } from "../../lib/queryKeys";
import { usePublicProvider } from "../../request-records/hooks/usePublicProvider";
import { ProviderRecord, InstitutionRecord } from "../../api/provider.type";

/**
 * Breadcrumb navigation component that builds links from the current URL path.
 */
export default function Breadcrumbs() {
  const location = useLocation();
  const segments = location.pathname.split("/").filter(Boolean);
  const userSegmentIndex = segments.indexOf("user");
  const contactUserId =
    userSegmentIndex >= 0 ? segments[userSegmentIndex + 1] : undefined;
  const contentId =
    segments.includes("content") &&
    segments.length > segments.indexOf("content") + 1
      ? segments[segments.indexOf("content") + 1]
      : "";
  const groupChatSegmentIndex = segments.indexOf("group-chat");
  const groupChatId =
    groupChatSegmentIndex >= 0 &&
    segments.length > groupChatSegmentIndex + 1
      ? segments[groupChatSegmentIndex + 1]
      : undefined;
  const requestRecordsSegmentIndex = segments.indexOf("request-records");
  const requestRecordSlug =
    requestRecordsSegmentIndex >= 0 &&
    segments.length > requestRecordsSegmentIndex + 1
      ? segments[requestRecordsSegmentIndex + 1]
      : undefined;

  const { content } = useLoadContentByIdOrSlug(contentId, {
    enabled: !!contentId,
  });

  const { user } = useAuth();

  const { data: contacts } = useQuery<ContactRow[]>({
    queryKey: ["contacts", user?.id],
    queryFn: getContacts,
    enabled: Boolean(contactUserId && user?.id),
    staleTime: 5 * 60 * 1000,
  });

  const { data: groupChat } = useQuery({
    queryKey: qk.groupChat(groupChatId ?? ""),
    queryFn: () => getGroupChat(groupChatId ?? ""),
    enabled: Boolean(groupChatId),
    staleTime: 5 * 60 * 1000,
  });

  const { data: requestProvider } = usePublicProvider(requestRecordSlug);

  const contactDisplayName = useMemo(() => {
    if (!contactUserId) return undefined;
    const match = contacts?.find(
      (contact) => contact.related_user_id === contactUserId
    );
    if (match?.related_user) {
      const { first_name, last_name, email } = match.related_user;
      const fullName = `${first_name ?? ""} ${last_name ?? ""}`.trim();
      return fullName || email || "User Records";
    }
    return undefined;
  }, [contactUserId, contacts]);

  const requestProviderDisplayName = useMemo(() => {
    if (!requestProvider || !requestRecordSlug) return undefined;
    if (requestProvider.type === "provider") {
      const record = requestProvider.record as ProviderRecord;
      const fullName = `${record.first_name ?? ""} ${record.last_name ?? ""}`.trim();
      return record.name || fullName || "Provider";
    }
    const record = requestProvider.record as InstitutionRecord;
    return record.name || "Institution";
  }, [requestProvider, requestRecordSlug]);

  const labelMap: Record<string, string> = {
    "medical-records": "Medical Records",
    "request-records": "Request Records",
    chat: "AI Chat",
    "group-chat": "Group Chat",
    "group-chats": "Group Chats",
    diagnosis: "Diagnoses",
    medication: "Medications",
    provider: "Providers",
    test: "Tests",
    vital: "Vitals",
    visit: "Visits",
  };

  const detailSectionMap: Record<string, { section: string; label: string }> = {
    diagnoses: { section: "diagnosis", label: labelMap.diagnosis },
    medications: { section: "medication", label: labelMap.medication },
    visits: { section: "visit", label: labelMap.visit },
    vitals: { section: "vital", label: labelMap.vital },
    providers: { section: "provider", label: labelMap.provider },
    "diagnostic-tests": { section: "test", label: labelMap.test },
  };

  const buildSectionPath = (section: string) => {
    if (contactUserId) {
      return `/medical-records/user/${contactUserId}/section/${section}`;
    }
    return `/medical-records/section/${section}`;
  };

  let path = "";
  type Breadcrumb = {
    label: string;
    path: string;
  };

  const breadcrumbs = segments
    .map((segment, index) => {
      path += `/${segment}`;

      // Skip rendering the "section" and "user" segments
      if (segment === "section" || segment === "user") {
        return null;
      }

      // If this is a userId, try to get the user's name
      if (segments[index - 1] === "user") {
        return {
          label: contactDisplayName || "User Records",
          path,
        };
      }

      if (segments[index - 1] === "group-chat") {
        return {
          label: groupChat?.name || "Conversation",
          path,
        };
      }

      // If this is a section name, use the label map
      if (segments[index - 1] === "section") {
        return {
          label: labelMap[segment] || toTitleCase(segment),
          path,
        };
      }

      // If this is a content id or slug, use the loaded content title
      if (segments[index - 1] === "content" && content?.title) {
        return { label: content.title, path };
      }

      // Special handling for group-chat routes - navigate to groups list
      if (segment === "group-chat") {
        return {
          label: labelMap[segment] || segment,
          path: "/group-chats",
        };
      }

      if (
        segment === "media" &&
        segments[index - 1] === "content" &&
        segments[index - 2] === "admin"
      ) {
        return {
          label: "Media",
          path: "/admin/content?tab=media#media",
        };
      }

      if (detailSectionMap[segment]) {
        const detailConfig = detailSectionMap[segment];
        return {
          label: detailConfig.label || toTitleCase(segment),
          path: buildSectionPath(detailConfig.section),
        };
      }

      // Special handling for request-records: navigate to public harper.new domain
      if (segment === "request-records") {
        return {
          label: labelMap[segment] || segment,
          path: "https://harper.new/get-your-medical-records",
        };
      }

      if (segments[index - 1] === "request-records") {
        return {
          label: requestProviderDisplayName || toTitleCase(segment),
          path,
        };
      }

      // For other segments, use the label map or the segment itself
      return {
        label: labelMap[segment] || toTitleCase(segment),
        path,
      };
    })
    .filter((crumb): crumb is Breadcrumb => crumb !== null);

  return (
    <nav className="flex mt-4" aria-label="Breadcrumb">
      <ol className="flex items-center space-x-2">
        <li>
          <Link to="/" className="text-gray-500 hover:text-gray-700">
            Home
          </Link>
        </li>
        {breadcrumbs.map((crumb, index) => (
          <li key={crumb.path} className="flex items-center">
            <span className="mx-2 text-gray-500">/</span>
            <Link
              to={crumb.path}
              className={`${
                index === breadcrumbs.length - 1
                  ? "text-terracotta font-medium"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {crumb.label}
            </Link>
          </li>
        ))}
      </ol>
    </nav>
  );
}
